import Button from "~/components/ui/Button"
import { invoke } from "~/runtime"
import { useSignal } from "~/sdk/signal"
import type React from "react"
import { useRef } from "react"
import { Ref } from "react"

interface InputProps {
  id: string
  placeholder: string
  label: string
  inputRef: Ref<HTMLInputElement>
  error: string
}

function Input({ id, placeholder, label, inputRef, error }: InputProps) {
  return (
    <div className='w-full flex flex-col gap-2'>
      <div className='flex w-full justify-between items-center'>
        <label
          htmlFor={id}
          className='text-[#361328] text-sm'
        >
          {label}
        </label>
        <span className='text-[#91153A] text-xs'>{error}</span>
      </div>

      <input
        id={id}
        ref={inputRef}
        className='h-11 w-full text-sm text-[#494949] placeholder:text-[#494949] bg-white border border-white shadow-md shadow-[#00000019] px-4 duration-300 hover:border-[#CACACA] rounded-lg outline-none focus:border-[#CACACA]'
        placeholder={placeholder}
      />
    </div>
  )
}

export interface Props {
  /**
   * @title Título
   */
  /**
   * @description Se não for passado um título, será renderizado o default ("Não encontrou a marca que estava procurando?")
   */
  title?: string
  /**
   * @title Subtítulo
   */
  /**
   * @description Se não for passado um subtítulo, será renderizado o default ("Compartilhe conosco a sua sugestão, iremos fazer o possível para a marca estar aqui na sua próxima compra!")
   */
  subtitle?: string
  /**
   * @title Texto do botão de enviar formulário
   */
  /**
   * @description Se não for passado um texto para o botão, será renderizado o default ("Enviar")
   */
  submitButton?: string
}

function GlossaryContact({ title, subtitle, submitButton }: Props) {
  const titleText = title ?? 'Não encontrou a marca que estava procurando?'
  const subtitleText = subtitle ??
    'Compartilhe conosco a sua sugestão, iremos fazer o possível para a marca estar aqui na sua próxima compra!'
  const submitText = submitButton ?? 'Enviar'

  const emailInputRef = useRef<HTMLInputElement>(null)
  const suggestionInputRef = useRef<HTMLInputElement>(null)
  const errors = useSignal({ email: '', suggestion: '' })
  const feedbackMessage = useSignal('')

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const email = emailInputRef.current?.value
    const suggestion = suggestionInputRef.current?.value

    errors.value = { email: '', suggestion: '' }

    if (!email) {
      errors.value.email = 'Insira um e-mail.'
      return
    }

    if (!isEmailValid()) {
      errors.value.email = 'Insira um e-mail válido.'
      return
    }

    if (!suggestion) {
      errors.value.suggestion = 'Insira um nome.'
      return
    }

    const data = {
      email,
      suggestion,
    }

    await invoke.vtex.actions.masterdata.createDocument({
      acronym: 'GB',
      data,
    }).then(() => feedbackMessage.value = 'success').catch((error) => {
      console.error('ERROR', error)
      feedbackMessage.value = 'error'
    })
  }

  function isEmailValid() {
    const email = emailInputRef.current?.value
    return email?.match(/^[^@]+@[^@]+\.[^@]+$/)
  }

  return (
    <div className='w-full max-w-[1800px] mx-auto px-4 mt-11 mb-12 lg:mb-16'>
      <div className='w-full bg-[#F9F9F9] border border-[#EBEBEB] rounded-lg p-5 min-h-32 flex items-center justify-center'>
        {feedbackMessage.value === 'success' && (
          <div className='w-full flex flex-col items-center gap-2'>
            <span className='text-xl font-extrabold text-red-wine text-center'>
              Recebemos a sua sugestão!
            </span>
            <span className='text-[#585858] text-center'>
              Quando esta marca for nossa parceira te avisaremos por e-mail!
            </span>
          </div>
        )}

        {feedbackMessage.value === 'error' && (
          <div className='w-full flex flex-col items-center gap-2'>
            <span className='text-xl font-extrabold text-[#585858] text-center'>
              Mensagem não enviada...
            </span>
            <span className='text-[#585858] text-center'>
              Tente novamente mais tarde!
            </span>
          </div>
        )}

        {!feedbackMessage.value && (
          <form
            onSubmit={onSubmit}
            className='w-full max-w-[1306px] mx-auto flex flex-col lg:flex-row gap-5 lg:gap-10 xl:gap-16 items-center'
          >
            <div className='w-full lg:w-auto lg:max-w-[469px] flex flex-col gap-2'>
              <span className='text-xl font-extrabold text-red-wine'>{titleText}</span>
              <span className='text-[#585858]'>{subtitleText}</span>
            </div>

            <div className='w-full lg:w-auto flex flex-col md:flex-row gap-5 md:gap-4 xl:gap-5 flex-1'>
              <Input
                id='email'
                label='Seu e-mail:'
                placeholder='insira seu e-mail'
                inputRef={emailInputRef}
                error={errors.value.email}
              />

              <Input
                id='suggestion'
                label='Sugestão de marca:'
                placeholder='insira aqui o nome da marca que deseja'
                inputRef={suggestionInputRef}
                error={errors.value.suggestion}
              />

              <div className='flex items-end'>
                <Button
                  type='submit'
                  className='w-full md:w-auto h-11 min-h-11 bg-[#E5554F] border border-[#E5554F] text-white duration-300 hover:bg-[#BF241E] hover:border-[#BF241E] uppercase font-bold rounded-lg'
                >
                  {submitText}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default GlossaryContact
